import * as Yup from 'yup';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { FormErrorMessage, Select } from '@treams/treams-ui';

import { IOption } from '../../../packages/kakapo-ui-services/types/Option';
import { ISheetEditorEvents } from '../types/sheet';

const BulkSelect = ({
	value,
	events,
	validationSchema,
	options,
}: {
	value: string;
	events: ISheetEditorEvents;
	validationSchema?: Yup.StringSchema;
	options: IOption<void>[];
}) => {
	const invalid = validationSchema ? !validationSchema.isValidSync(value) : false;

	return (
		<>
			<Select
				options={options.map((option) => ({
					title: option.label,
					value: option.value,
				}))}
				selectedValue={value}
				onChange={(value) => events.onChange(value)}
				small
				autofocus
			/>
			{invalid && <FormErrorMessage error={new Error('Field invalid.')} />}
		</>
	);
};

export const useDepartmentOptions = (): [
	IOption<void>[] | undefined,
	(valueOrTitle: string) => string | undefined,
] => {
	const { data: groupsData } = graphql.useOrganizationGroupsQuery({
		variables: {
			filter: {
				types: [graphql.GroupType.Department, graphql.GroupType.Region],
			},
			first: 10000,
		},
		fetchPolicy: 'cache-and-network',
	});

	const options = groupsData?.groups.edges.map(({ node }) => ({
		label: node.name,
		value: node.id,
	}));

	const valueFromTitle = (valueOrTitle: string): string | undefined => {
		return options?.find((option) => option.value === valueOrTitle || option.label === valueOrTitle)
			?.value;
	};

	return [options, valueFromTitle];
};

export const BulkSelectDepartment = ({
	value,
	events,
}: {
	value: string;
	events: ISheetEditorEvents;
}) => {
	const [options, valueFromTitle] = useDepartmentOptions();
	if (!options) {
		return <p>Loading...</p>;
	}

	return <BulkSelect options={options} events={events} value={valueFromTitle(value) ?? ''} />;
};

export const useJobTitleOptions = (): [
	IOption<void>[] | undefined,
	(valueOrTitle: string) => string | undefined,
] => {
	const { localize } = useLocalize();

	const { data: jobTitlesData } = graphql.useJobTitlesQuery({
		variables: {
			first: 10000,
		},
		fetchPolicy: 'cache-and-network',
	});

	const options = jobTitlesData?.jobTitles.edges.map(({ node }) => ({
		label: localize(node.title).text,
		value: node.id,
	}));

	const valueFromTitle = (valueOrTitle: string): string | undefined => {
		return options?.find((option) => option.value === valueOrTitle || option.label === valueOrTitle)
			?.value;
	};

	return [options, valueFromTitle];
};

export const BulkSelectJobTitle = ({
	value,
	events,
}: {
	value: string;
	events: ISheetEditorEvents;
}) => {
	const [options, valueFromTitle] = useJobTitleOptions();
	if (!options) {
		return <p>Loading...</p>;
	}

	return <BulkSelect options={options} events={events} value={valueFromTitle(value) ?? ''} />;
};

export default BulkSelect;
